/* The card on the desk — up to `--desk-measure` wide, its head and its action bar fixed, its body
 * scrolling between them. Direction B2C (owner, 5 November 2026): the card keeps its box and shadow,
 * its bottom corners square where the decisions join its foot, and C2's air inside it — the head
 * 28/32/24, the body and acts 32 off the sides.
 *
 * Canon: `zadania-c-runda5.html`, "C, deepened" and "C, round 3". The two decision buttons are NOT
 * in here: they stand alone under the card (owner, 18 September 2026 — "Później" and "Gotowe" speak
 * about the trainer's own list, the buttons inside the card speak about the content). */

.card {
  position: relative;
  z-index: 1;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-2xl) var(--radius-2xl) 0 0;
  box-shadow: var(--shadow-card);
}

/* ── The head ──────────────────────────────────────────────────────────────────────────────── */

.head {
  display: flex;
  flex: none;
  gap: var(--space-4);
  align-items: center;
  padding: var(--space-7) var(--space-8) var(--space-6);
  border-bottom: var(--border-hairline) solid var(--border);
}

/* The card's avatar is 48. `AvatarChip` owns every other declaration; a surface may state its ONE
 * number through `--sz-avatar`, which is the seam that file leaves and the only one. */
.avatar {
  --sz-avatar: 48px;

  background: var(--bg-card);
}

.headText {
  min-width: 0;
}

.name {
  overflow: hidden;
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-heading);
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--text);
}

.when {
  margin-top: var(--space-1px);
  font-size: var(--type-control);
  color: var(--text-2);
}

/* The position chip. Tabular figures, so "4 z 14" and "11 z 14" are the same width and the chip
 * does not resize as the trainer works down the deck.
 *
 * The canon's `.focus .fh .chip`: 22px tall, 9px of side padding, 11px/700. It was carrying 2.5px
 * of padding and 10px type, which left the digits sitting on a chip ground narrower than they
 * were — from across the desk it read as bare text rather than as a chip. */
.position {
  display: inline-flex;
  flex: none;
  gap: var(--space-6px);
  align-items: center;
  height: 22px;
  padding: var(--space-0) var(--space-9px);
  margin-left: auto;
  font-size: var(--fs-label);
  font-weight: var(--fw-medium);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--text-2);
  background: var(--bg-chip);
  border-radius: var(--r-pill);
}

/* ── The body ──────────────────────────────────────────────────────────────────────────────── */

/* Every kind is read whole at 1512×862 in its resting state (owner, 25 September 2026): the head's
 * and the body's own padding were cut by a step each for it, `e2e/viewport-fit.spec.ts` measures it. */

.body {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: var(--space-4);
  min-height: 0;
  padding: var(--space-2) var(--space-8) var(--space-5);
  overflow-y: auto;
}

/* More of the body is hidden below: its last 48px fade out, so a trainer on a 620px window can see
 * there is more before reaching for a scrollbar the OS may not draw. A mask, not a painted gradient:
 * it moves no layout and needs no colour stop per theme, and it comes off at the end of the scroll
 * (`useOverflowEdge`). It is a state, not a motion, so there is nothing for reduced motion to stop. */
.bodyMore {
  mask-image: linear-gradient(180deg, rgb(0 0 0) calc(100% - var(--space-12)), rgb(0 0 0 / 0%) 100%);
}

/* ── The action bar ────────────────────────────────────────────────────────────────────────── */

/* Pinned at the card's foot, so the primary is reachable however long the body is. The card is
 * bounded by the grid row above it and the body scrolls, which is the fit-or-scroll rule: nothing
 * here is ever clipped without a scroll path. */
.cta {
  display: flex;
  flex: none;
  gap: var(--space-2);
  align-items: center;
  padding: var(--space-3) var(--space-8);
  background: transparent;
  border-top: var(--border-hairline) solid var(--border);
  border-radius: 0 0 var(--radius-2xl) var(--radius-2xl);
}

/* `inkButton` and `ghostButton` are TREATMENTS — fill, ink, edge, focus ring — and the SURFACE
 * declares the geometry, which is the split every button in this app follows. Without these two the
 * primary rendered as a black rectangle with no padding and no radius. */
.primary,
.secondary {
  /* The canon's own `.btn`: 34 tall, 13 of side padding, radius 10, 12.5/700. The height is a
   * layout value; every colour, space, radius and type value here is a token. The gap is the one
   * between an act's glyph and its word. */
  gap: var(--space-6px);
  height: 34px;
  padding: var(--space-0) var(--space-3);
  font-size: var(--type-control);
  font-weight: var(--fw-medium);
  border-radius: var(--radius-sm);
}

/* B2C: the card's one ink act is the app's ember pill — the Pulpit's paint, flat. Its geometry holds
 * in every state; the paint only while it can be pressed, so a disabled primary keeps the ink
 * skin's grey. The `:not()` is also what outranks `shared/ui/inkButton`'s fill and ink by
 * specificity rather than by stylesheet order. */
.primary {
  padding-inline: var(--space-4);
  font-weight: var(--fw-semibold);
  border-radius: var(--r-pill);
}

.primary:not(:disabled, [aria-disabled='true']) {
  color: var(--on-accent-grad);
  background: var(--accent-grad-diagonal);
  border-color: transparent;
}

/* The ghost's fill is the surface's to declare (`ghostButton.module.css`), and here it is none: the
 * foot's own ground shows through. A `<button>` that declares none is painted the browser's grey, so
 * „Zobacz sesję" read as pressed beside the links. ONLY the ghost: the ink primary's fill is its
 * treatment's, and a `background` declared on both sides is settled by chunk order — the build drew
 * „Oznacz jako przejrzane" with no body, dark ink on the dark foot. */
.secondary {
  background: none;
}

/* The quiet secondary: ink words, its glyph a step back. Same specificity reason as above. */
.secondary:not(:disabled, [aria-disabled='true']) {
  color: var(--text);
}

.secondary svg {
  color: var(--text-2);
}

/* Pushes the way out to the bar's far end, the canon's `.k-sp`. */
.spacer {
  flex: 1;
}

/* The refusal's sentence, VISIBLE and the element `aria-describedby` points at. A `title` reaches
 * neither a keyboard nor a touch pointer, and a greyed pill with no readable reason beside it is
 * the failure the whole refused construction exists to avoid. */
.refusal {
  flex: 1;
  min-width: 0;
  font-size: var(--fs-label);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* What the trainer already answered about this card, stated on the card itself — the index's strike
 * is across the room, and this is where they are looking. */
.state {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
  margin-left: auto;
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  white-space: nowrap;
  color: var(--text-2);
}

.stateMark {
  width: 14px;
  height: 14px;
}
